import { useEffect, useRef, useState } from "react";
import HeroSection from "./components/HeroSection";
import EmpathySection from "./components/EmpathySection";
import HowSupportWorks from "./components/HowSupportWorks";
import AreasOfSupport from "./components/AreasOfSupport";
import EveningRoutine from "./components/EveningRoutine";
import MeetTheGuide from "./components/MeetTheGuide";
import GentleTools from "./components/GentleTools";
import Testimonials from "./components/Testimonials";
import FinalCTA from "./components/FinalCTA";
import SEOMeta from "@/components/base/SEOMeta";

export default function Home() {
  const contactRef = useRef<HTMLDivElement>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px 50px 0px" }
    );

    const sections = document.querySelectorAll(".scroll-reveal");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://serenasquietrecoveryspace.com/#webpage",
    "name": "Serena's Quiet Recovery Space | Private Alcohol Recovery Support",
    "description": "A private, judgement-free space to understand your relationship with alcohol, explore positive change and access supportive recovery resources at your own pace. Gentle coaching from Serena Raimes.",
    "url": "https://serenasquietrecoveryspace.com/",
    "about": {
      "@type": "Organization",
      "name": "Serena's Quiet Recovery Space"
    },
    "primaryImageOfPage": {
      "@type": "ImageObject",
      "url": "https://storage.readdy-site.link/project_files/1f18a76f-ed3f-4e88-bb38-b62c775c3336/dd8305a0-1fac-4e33-aa4a-430f46df46f4_ChatGPT-Image-Jun-9-2026-03_24_23-PM.png?v=ad55fcfd6c6876a070c9edc39cde37f1"
    }
  };

  return (
    <main className="min-h-screen">
      <SEOMeta
        title="Private Alcohol Recovery Support | Serena's Quiet Recovery Space"
        description="A private, judgement-free space to understand your relationship with alcohol, explore positive change and access supportive recovery resources at your own pace. Gentle coaching from Serena Raimes."
        canonicalUrl="/"
        ogType="website"
        schema={pageSchema}
      />
      <HeroSection />

      <div className="scroll-reveal fade-up">
        <EmpathySection />
      </div>

      <div className="scroll-reveal fade-up">
        <HowSupportWorks />
      </div>

      <div className="scroll-reveal fade-up">
        <AreasOfSupport />
      </div>

      <div className="scroll-reveal fade-up">
        <EveningRoutine />
      </div>

      <div className="scroll-reveal fade-up">
        <MeetTheGuide />
      </div>

      <div className="scroll-reveal fade-up">
        <GentleTools />
      </div>

      <div className="scroll-reveal fade-up">
        <Testimonials />
      </div>

      <div className="scroll-reveal fade-in">
        <FinalCTA />
      </div>

      {/* Contact Form Section */}
      <section
        ref={contactRef}
        id="contact-form"
        className="w-full bg-background-100 py-16 md:py-24 scroll-reveal fade-up"
      >
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start">
              <div className="w-full md:w-1/2">
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-foreground-950 mb-4">
                  Start a quiet conversation
                </h2>
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-2 max-w-md">
                  Share a little about your situation and what kind of support you are looking for. Every conversation is confidential and without obligation.
                </p>
                <p className="font-body text-xs text-foreground-500 mb-8">
                  Private &bull; Supportive &bull; Non-judgemental
                </p>

                <form
                  id="contact-form-main"
                  action="https://readdy.ai/api/form/d8k2i9m0gbrp57rm5hjg"
                  method="POST"
                  data-readdy-form
                  className="space-y-5 max-w-md"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);
                    try {
                      const res = await fetch(form.action, {
                        method: "POST",
                        body: formData,
                        headers: {
                          Accept: "application/json",
                        },
                      });
                      if (res.ok) {
                        setFormSubmitted(true);
                        setFormError(false);
                        form.reset();
                      } else {
                        setFormError(true);
                        setFormSubmitted(false);
                      }
                    } catch {
                      setFormError(true);
                      setFormSubmitted(false);
                    }
                  }}
                >
                  <div>
                    <label
                      htmlFor="name"
                      className="block font-body text-sm font-medium text-foreground-800 mb-2"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block font-body text-sm font-medium text-foreground-800 mb-2"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block font-body text-sm font-medium text-foreground-800 mb-2"
                    >
                      Your message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300 resize-none"
                      placeholder="Tell me a little about where you are and what kind of support might help..."
                    />
                    <p className="mt-1 text-xs font-body text-foreground-400">
                      Maximum 500 characters
                    </p>
                  </div>

                  {formSubmitted && (
                    <div className="bg-primary-100 text-primary-800 font-body text-sm px-4 py-3 rounded-md">
                      Thank you for reaching out. We'll reply within two business days.
                    </div>
                  )}
                  {formError && (
                    <div className="bg-accent-100 text-accent-800 font-body text-sm px-4 py-3 rounded-md">
                      Something went wrong. Please try again or email us directly.
                    </div>
                  )}

                  <button
                    type="submit"
                    className="bg-primary-500 text-background-50 font-body text-sm font-medium px-8 py-3 rounded-md hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Send message
                  </button>
                </form>
              </div>

              <div className="w-full md:w-1/2">
                <div className="relative overflow-hidden rounded-lg">
                  <img
                    src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/df335f699e9f61bda7803626e38b2b52.png"
                    alt="A cosy home desk with laptop, notebook, tea, and soft lamp - calm and private atmosphere"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .scroll-reveal {
          opacity: 0;
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal.fade-up {
          transform: translateY(32px);
        }
        .scroll-reveal.fade-in {
          transform: none;
        }
        .animate-in {
          opacity: 1 !important;
          transform: translate(0, 0) !important;
        }

        .stagger-children > * {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.55s cubic-bezier(0.16, 1, 0.3, 1), transform 0.55s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .animate-in .stagger-children > *:nth-child(1)  { transition-delay: 0ms; }
        .animate-in .stagger-children > *:nth-child(2)  { transition-delay: 80ms; }
        .animate-in .stagger-children > *:nth-child(3)  { transition-delay: 160ms; }
        .animate-in .stagger-children > *:nth-child(4)  { transition-delay: 240ms; }
        .animate-in .stagger-children > *:nth-child(5)  { transition-delay: 320ms; }
        .animate-in .stagger-children > *:nth-child(6)  { transition-delay: 400ms; }
        .animate-in .stagger-children > *:nth-child(7)  { transition-delay: 480ms; }
        .animate-in .stagger-children > *:nth-child(8)  { transition-delay: 560ms; }
        .animate-in .stagger-children > *:nth-child(9)  { transition-delay: 640ms; }
        .animate-in .stagger-children > *:nth-child(10) { transition-delay: 720ms; }
        .animate-in .stagger-children > * {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </main>
  );
}